import React from 'react'

import type { IntakeAnswers } from '../lib/types'
import type { QuestionDef } from '../questions/types'
import { CollapsedQuestionRow } from './CollapsedQuestionRow'
import { IntakeQuestion } from './IntakeQuestion'
import { useExpandCollapse } from './useExpandCollapse'

/**
 * One question's row, animating between its collapsed (answered) summary and
 * its full editable control. Both variants stay mounted at all times —
 * `useExpandCollapse` needs a persistent wrapper to animate the height
 * across — so answering (or reopening) a question reads as the summary row
 * growing/shrinking in place rather than an abrupt layout cut.
 */
export const QuestionRow: React.FC<{
  def: QuestionDef
  value: unknown
  allAnswers: IntakeAnswers
  isCollapsed: boolean
  isReopened: boolean
  onToggle: () => void
  onAnswer: (key: string, value: unknown) => void
  onInteract: (key: string) => void
  /** Set for a question that has to be confirmed before it counts as done.
   *  Takes the place of "Done editing" — one affordance, not two. */
  onConfirm?: () => void
  /** Whether that confirm control is inert, because there is nothing to
   *  confirm yet. */
  confirmDisabled?: boolean
  hasError?: boolean
}> = ({
  def,
  value,
  allAnswers,
  isCollapsed,
  isReopened,
  onToggle,
  onAnswer,
  onInteract,
  onConfirm,
  confirmDisabled,
  hasError,
}) => {
  const collapsedRef = useExpandCollapse(isCollapsed)
  const expandedRef = useExpandCollapse(!isCollapsed)
  return (
    <div className="flex flex-col">
      <div ref={collapsedRef}>
        <CollapsedQuestionRow def={def} value={value} onExpand={onToggle} />
      </div>
      <div ref={expandedRef}>
        <IntakeQuestion
          def={def}
          allAnswers={allAnswers}
          onAnswer={onAnswer}
          onInteract={onInteract}
          hasError={hasError}
          asCard
          onConfirm={onConfirm}
          confirmDisabled={confirmDisabled}
          onDoneEditing={!onConfirm && isReopened ? onToggle : undefined}
        />
      </div>
    </div>
  )
}
